<script setup lang="ts">
  import { ref, useTemplateRef } from 'vue'
  import type { QuickContact } from '@gat-ship/types'
  import { IconPlus, IconTrash } from '@gat-ship/icons'
  import ContactModalAddQuick from './modals/ContactModalAddQuick.vue'
  import { DataListItemAction, Button } from '@gat-ship/vue-components'
  import ContactPersonListItem from './list/ContactPersonListItem.vue'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'

  withDefaults(
    defineProps<{
      /** The maximum number of contacts that can be added.
       * @default 5
       */
      maxContacts?: number
    }>(),
    {
      maxContacts: 50
    }
  )

  const contacts = defineModel<QuickContact[]>('contacts', {
    default: () => []
  })

  const addContactModalRef = useTemplateRef('addContactModal')

  const showConfirm = ref(false)
  const pendingRemoveContact = ref<QuickContact>()

  function addContact(contact: QuickContact) {
    const idx = contacts.value?.findIndex((c) => c.id === contact.id)
    if (idx !== undefined && idx !== -1) {
      contacts.value[idx] = contact
    } else {
      contacts.value.push(contact)
    }
  }

  function removeContact(contact: QuickContact) {
    pendingRemoveContact.value = contact
    showConfirm.value = true
  }

  function onConfirmRemove() {
    showConfirm.value = false
    if (pendingRemoveContact.value) {
      contacts.value = contacts.value?.filter(
        (c) => c.id !== pendingRemoveContact.value!.id
      )
    }
  }

  function editContact(contact: QuickContact) {
    addContactModalRef.value?.showModal({
      contact: contact
    })
  }
</script>

<template>
  <ContactModalAddQuick ref="addContactModal" @added="addContact" />
  <div class="flex flex-col divide-y divide-neutral-300">
    <template v-for="contact in contacts" :key="contact.id">
      <ContactPersonListItem
        :contact="{
          contactType: 'person',
          firstName: contact.firstName,
          lastName: contact.lastName,
          jobTitle: contact.jobTitle,
          email: contact.email,
          directLine: contact.directLine,
          mobilePhone: contact.mobilePhone,
          department: contact.department,
          id: contact.id,
          stakeholderId: '',
          stakeholderName: '',
          stakeholderShortName: '',
          stakeholderType: 'client',
          isInactive: false
        }"
        @click="editContact(contact)"
      >
        <template #actions
          ><DataListItemAction
            label="Remove"
            variant="ghost"
            size="icon"
            :icon="IconTrash"
            @click.stop="removeContact(contact)"
          />
        </template>
      </ContactPersonListItem>
    </template>
  </div>
  <Button
    :disabled="contacts.length >= maxContacts"
    variant="outline"
    class="text-muted-foreground"
    @click="
      addContactModalRef?.showModal({
        contact: {
          firstName: '',
          lastName: '',
          jobTitle: null,
          email: null,
          directLine: null,
          mobilePhone: null,
          department: null
        }
      })
    "
  >
    <IconPlus class="size-4" />Add Contact
  </Button>
  <AlertDialog v-model:open="showConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove Contact</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to remove
          {{ pendingRemoveContact?.firstName }}
          {{ pendingRemoveContact?.lastName }}?
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="onConfirmRemove()"> Confirm </Button>
        <Button variant="outline" @click="showConfirm = false"> Cancel </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
